/**
 * Format a number as a currency string. Default 'ILS' preserves every legacy
 * call site that omits the second argument (most of the codebase).
 */
export function formatCurrency(amount: number, currency = 'ILS'): string {
  return new Intl.NumberFormat('he-IL', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Lookup for currency symbols — used wherever the UI needs just the glyph
 * (e.g. inline next to a numeric input) without going through Intl. Keeps the
 * `₪`/`$`/`€`/`£` rendering decision in ONE place so we don't end up with
 * a dozen ad-hoc `currency === 'USD' ? '$' : '₪'` ternaries across the app.
 */
export const CURRENCY_SYMBOL: Record<string, string> = {
  ILS: '₪',
  USD: '$',
  EUR: '€',
  GBP: '£',
};

export function currencySymbol(currency?: string): string {
  return CURRENCY_SYMBOL[(currency || 'ILS').toUpperCase()] || '₪';
}

/**
 * Format a date as Israeli locale date string
 */
export function formatDate(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('he-IL');
}

/**
 * Truncate a string to maxLength and add ellipsis
 */
export function truncate(str: string, maxLength: number): string {
  if (str.length <= maxLength) return str;
  return str.slice(0, maxLength) + '...';
}

/**
 * Player/download URL for a call recording. Twilio recordings live at
 * .../RExxx and need ".mp3" appended; everyone else (Telnyx, Bezeq via
 * S3 presigned URL) hands us the file URL as-is — appending ".mp3" to a
 * presigned URL breaks the signature.
 */
export function recordingHref(url: string): string {
  return /\.twilio\.com\//i.test(url) ? `${url}.mp3` : url;
}

/**
 * Format phone number to Israeli display format
 */
export function formatPhone(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  if (digits.startsWith('972') && digits.length === 12) {
    return `0${digits.slice(3, 5)}-${digits.slice(5, 8)}-${digits.slice(8)}`;
  }
  if (digits.length === 10 && digits.startsWith('0')) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return phone;
}
